import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { EditPencil } from "components/icons/EditPencil";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { FormikValues } from "formik";
import { useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import {
  useCreateAutofillMutation,
  useGetAutofillQuery,
  useUpdateAutofillMutation,
} from "store/apis/autofill";
import { PersonaAutofill } from "types/Models/personaAutofill";
import { BackendError } from "types/utils/error";
import { PersonaDetailsModal } from "./PersonaDetailsModal";

const personInitials = (firstName?: string, lastName?: string) =>
  `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();

const personaName = (persona: PersonaAutofill) =>
  `${persona.first_name} ${persona.last_name}`.trim();

type PersonaComboboxProps = {
  entityId: string;
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  selected?: string;
  /** Keeps the selection fixed (can't reassign to a different person) but
   * still offers the pencil to edit that person's own details - used when
   * editing an existing role, where who the role belongs to can't change. */
  locked?: boolean;
  /** Shown while `selected`'s full record hasn't loaded yet - falls back to
   * data already available to the caller (e.g. from the role being edited). */
  lockedDisplayName?: string;
  onSelect: (persona: PersonaAutofill) => void;
  onClear: () => void;
};

export const PersonaCombobox = ({
  entityId,
  name,
  label,
  placeholder = "Select or add a new person",
  required,
  selected,
  locked,
  lockedDisplayName,
  onSelect,
  onClear,
}: PersonaComboboxProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const personaModal = useModal();
  const [editingPersona, setEditingPersona] = useState<PersonaAutofill>();

  const { data: personas = [], isLoading } = useGetAutofillQuery(
    { groupId, autofillKey: "authorized_persons", entityId },
    { skip: !groupId || !entityId }
  );

  const [createAutofill, { isLoading: isCreating }] =
    useCreateAutofillMutation();
  const [updateAutofill, { isLoading: isUpdating }] =
    useUpdateAutofillMutation();

  const selectedPersona = personas.find((persona) => persona.uuid === selected);

  const options = personas.map((persona) => ({
    value: persona.uuid,
    label: personaName(persona),
  }));

  const openAddModal = () => {
    setEditingPersona(undefined);
    personaModal.open();
  };

  const openEditModal = (persona: PersonaAutofill) => {
    setEditingPersona(persona);
    personaModal.open();
  };

  const handleAdd = async (values: FormikValues) => {
    try {
      const persona = (await createAutofill({
        fields: values,
        groupId: groupId!,
        autofillKey: "authorized_persons",
        entityId,
      }).unwrap()) as PersonaAutofill;
      onSelect(persona);
      personaModal.close();
      successToast({ message: "Person added successfully" });
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handleChange = (
    option: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (option instanceof Array) return;
    if (!option) {
      onClear();
      return;
    }
    const persona = personas.find((p) => p.uuid === option.value);
    if (persona) onSelect(persona);
  };

  const handleEdit = async (values: FormikValues) => {
    try {
      const persona = (await updateAutofill({
        groupId: groupId!,
        autofillKey: "authorized_persons",
        fields: values,
        uuid: values.id,
        entityId,
      }).unwrap()) as PersonaAutofill;
      if (persona.uuid === selected) {
        onSelect(persona);
      }
      personaModal.close();
      successToast({ message: "Person updated successfully" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  if (locked) {
    return (
      <>
        <div className="t-flex t-flex-col t-gap-1">
          <div className="t-text-body-sm t-text-text-30">{label}</div>
          <div className="t-flex t-h-10 t-items-center t-justify-between t-rounded-md t-border t-border-solid t-border-neutral-10 t-bg-neutral-0 t-px-3 t-text-body t-text-neutral-40">
            <span>
              {selectedPersona
                ? personaName(selectedPersona)
                : lockedDisplayName}
            </span>
            <Button
              customType="ghost_icon"
              className="t-text-text-30 hover:t-text-text-60"
              type="button"
              onClick={() => selectedPersona && openEditModal(selectedPersona)}
            >
              <EditPencil />
            </Button>
          </div>
        </div>
        <PersonaDetailsModal
          entityId={entityId}
          open={personaModal.isOpen}
          onClose={personaModal.close}
          onSubmit={editingPersona ? handleEdit : handleAdd}
          isLoading={isCreating || isUpdating}
          initialValues={
            editingPersona ? { ...editingPersona, id: editingPersona.uuid } : {}
          }
        />
      </>
    );
  }

  return (
    <>
      <Combobox
        label={label}
        placeholder={placeholder}
        required={required}
        name={name}
        menuPortalTarget={document.body}
        isLoading={isLoading}
        options={options}
        value={
          selectedPersona
            ? {
                value: selectedPersona.uuid,
                label: personaName(selectedPersona),
              }
            : null
        }
        onChange={handleChange}
        actions={
          <Button
            customType="link"
            size="small"
            type="button"
            onClick={openAddModal}
          >
            Add new person
          </Button>
        }
        formatOptionLabel={(option, { context }) => {
          if (context === "value") {
            return <span>{option.label}</span>;
          }
          const persona = personas.find((p) => p.uuid === option.value);
          if (!persona) {
            return <span>{option.label}</span>;
          }
          return (
            <div className="t-flex t-w-full t-items-center t-justify-between t-gap-2">
              <div className="t-flex t-min-w-0 t-items-center t-gap-3">
                <span className="t-flex t-h-8 t-w-8 t-flex-shrink-0 t-items-center t-justify-center t-rounded-full t-bg-neutral-0 t-text-body-sm t-font-medium t-text-neutral-40">
                  {personInitials(persona.first_name, persona.last_name)}
                </span>
                <div className="t-flex t-min-w-0 t-flex-col">
                  <span className="t-truncate t-text-body t-text-text-60">
                    {personaName(persona)}
                  </span>
                  {persona.email && (
                    <span className="t-truncate t-text-body-sm t-font-light t-text-text-30">
                      {persona.email}
                    </span>
                  )}
                </div>
              </div>
              <Button
                customType="ghost_icon"
                className="t-text-text-30 hover:t-text-text-60"
                onClick={(e) => {
                  e.stopPropagation();
                  openEditModal(persona);
                }}
              >
                <EditPencil />
              </Button>
            </div>
          );
        }}
      />
      <PersonaDetailsModal
        entityId={entityId}
        open={personaModal.isOpen}
        onClose={personaModal.close}
        onSubmit={editingPersona ? handleEdit : handleAdd}
        isLoading={isCreating || isUpdating}
        initialValues={
          editingPersona ? { ...editingPersona, id: editingPersona.uuid } : {}
        }
      />
    </>
  );
};
